import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  NgApexchartsModule
} from 'ng-apexcharts';

import {MatCardModule} from '@angular/material/card';
import {TaskMetric} from "./task-metrics.model";

@Component({
  selector: 'app-task-report',
  standalone: true,
    imports: [MatCardModule, NgApexchartsModule],
  templateUrl: './task-report.component.html'
})
export class TaskReportComponent implements OnChanges {
  @Input() metricsData: TaskMetric[] = [];

  // --- Summary KPIs ---
  summary = {
    totalCalled: 0,
    totalCompleted: 0,
    totalFailed: 0,
    avgTaskTime: 0 // in seconds, whole number
  };

  // --- Timeline Chart (Avg Task Time Over Time) ---
  timelineSeries: ApexAxisChartSeries = [];
  timelineChart!: ApexChart;
  timelineXAxis!: ApexXAxis;
  timelineStroke!: ApexStroke;
  timelineTooltip!: ApexTooltip;

  // --- Issues Donut Chart (Failed vs. Successful Tasks) ---
  issuesSeries: ApexNonAxisChartSeries = [];
  issuesChart!: ApexChart;
  issuesLabels: string[] = [];
  issuesTooltip!: ApexTooltip;

  // --- Usage Stacked Bar Chart (Daily Totals) ---
  usageSeries: ApexAxisChartSeries = [];
  usageChart!: ApexChart;
  usageXAxis!: ApexXAxis;
  usagePlotOptions!: ApexPlotOptions;
  usageDataLabels!: ApexDataLabels;
  usageLegend!: ApexLegend;
  usageResponsive!: ApexResponsive[];

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['metricsData'] && this.metricsData.length) {
      this.computeSummary();
      this.buildTimelineChart();
      this.buildIssuesChart();
      this.buildUsageChart();
    }
  }

  private computeSummary(): void {
    const totals = this.metricsData.reduce(
        (acc, cur) => {
          acc.totalCalled += cur.totalTaskCalled;
          acc.totalCompleted += cur.totalTaskCompleted;
          acc.totalFailed += cur.totalTaskFailed;
          if (cur.totalTaskCompleted > 0) {
            acc.timeSum += cur.avgTaskTimeTaken * cur.totalTaskCompleted;
            acc.count += cur.totalTaskCompleted;
          }
          return acc;
        },
        { totalCalled: 0, totalCompleted: 0, totalFailed: 0, timeSum: 0, count: 0 }
    );

    this.summary.totalCalled = totals.totalCalled;
    this.summary.totalCompleted = totals.totalCompleted;
    this.summary.totalFailed = totals.totalFailed;
    // Compute weighted average task time in seconds, rounded.
    this.summary.avgTaskTime =
        totals.count > 0 ? Math.round(totals.timeSum / totals.count / 1000) : 0;
  }

  private buildTimelineChart(): void {
    // Group metrics by date (YYYY-MM-DD) and compute weighted average task time.
    const grouped: { [date: string]: { sum: number; count: number } } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { sum: 0, count: 0 };
      }
      if (metric.totalTaskCompleted > 0) {
        grouped[date].sum += metric.avgTaskTimeTaken * metric.totalTaskCompleted;
        grouped[date].count += metric.totalTaskCompleted;
      }
    });
    const dates = Object.keys(grouped).sort();
    const seriesData = dates.map(date =>
        grouped[date].count > 0
            ? Math.round(grouped[date].sum / grouped[date].count / 1000)
            : 0
    );

    this.timelineSeries = [
      { name: 'Avg Task Time (s)', data: seriesData }
    ];
    this.timelineChart = {
      type: 'line',
      height: 350,
      toolbar: { show: true }
    };
    this.timelineXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.timelineStroke = { curve: 'smooth', width: 3 };
    this.timelineTooltip = {
      y: { formatter: (val: number) => `${val} s` }
    };
  }

  private buildIssuesChart(): void {
    // For issues, consider successful tasks as (completed - failed).
    const totalCompleted = this.metricsData.reduce((sum, m) => sum + m.totalTaskCompleted, 0);
    const totalFailed = this.metricsData.reduce((sum, m) => sum + m.totalTaskFailed, 0);
    const successful = totalCompleted - totalFailed;

    this.issuesSeries = [totalFailed, successful];
    this.issuesLabels = ['Failed', 'Successful'];
    this.issuesChart = {
      type: 'donut',
      height: 350
    };
    this.issuesTooltip = {
      y: { formatter: (val: number) => val.toString() }
    };
  }

  private buildUsageChart(): void {
    // Group by report date to aggregate daily totals.
    const grouped: {
      [date: string]: {
        called: number;
        completed: number;
        failed: number;
      };
    } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { called: 0, completed: 0, failed: 0 };
      }
      grouped[date].called += metric.totalTaskCalled;
      grouped[date].completed += metric.totalTaskCompleted;
      grouped[date].failed += metric.totalTaskFailed;
    });

    const dates = Object.keys(grouped).sort();
    const calledSeries = dates.map(date => grouped[date].called);
    const completedSeries = dates.map(date => grouped[date].completed);
    const failedSeries = dates.map(date => grouped[date].failed);

    this.usageSeries = [
      { name: 'Called', data: calledSeries },
      { name: 'Completed', data: completedSeries },
      { name: 'Failed', data: failedSeries }
    ];
    this.usageChart = {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: { show: true }
    };
    this.usageXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.usagePlotOptions = {
      bar: { horizontal: false, columnWidth: '35%', borderRadius: 4 }
    };
    this.usageDataLabels = { enabled: false };
    this.usageLegend = { show: true };
    this.usageResponsive = [
      {
        breakpoint: 600,
        options: { plotOptions: { bar: { borderRadius: 3 } } }
      }
    ];
  }
}
